import React, { useState, useMemo, useCallback, useEffect, useRef, useLayoutEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { upsertMachine, deleteMachineApi, findMyRecentlyDeletedLogId, restoreDeletedRecord, getInventoryItems } from '../../lib/db';
import { toastUndo, toastError } from '../../lib/toast';
import { ACC, MUT, BRD, SURF, TXT, btnA, btnG, dvdr, sm, ovly, mdl, mdlH, mdlB, mdlF, inp } from '../../lib/styles';
import { getPref, savePref } from '../../lib/db/preferences';
import { getAllActiveBookings } from '../../lib/db/bookings';
import { getAllActiveCollections } from '../../lib/db/collections';
import MachineTile from '../machine/MachineTile';
import MachineRow from '../machine/MachineRow';
import MachinePhotoRow from '../machine/MachinePhotoRow';
import MachineCard from '../machine/MachineCard';
import { Empty } from '../ui/shared';
import MachineForm from '../machine/MachineForm';
import ErrorBoundary from '../ui/ErrorBoundary';
import GuestUpgradeModal from '../auth/GuestUpgradeModal';
import { machineMatchesQuery } from '../../lib/helpers';
import { tokenizeSearch, searchWiki } from '../../lib/wiki';

const _ARW = "#e8870a";
const _M = { fontFamily:"'IBM Plex Mono',monospace" };

// The arrow needs pixel precision (it has to actually land on the +Add
// button), so it's positioned via a measured anchor — but the button sits
// fairly close to the left edge on a narrow phone screen, so right-anchoring
// the TEXT below it the same way risked the text block overflowing past the
// left edge entirely (there isn't always 200px of room to its left). The
// text doesn't need pixel-perfect alignment the way the arrow does — it just
// needs to be legible — so it renders in normal document flow, left-aligned
// like the rest of the page, while only the arrow is absolutely positioned.
function GuideArrow({ anchor }) {
  if (!anchor) return null;
  return (
    <svg className="arrow-guide" width="130" height="140" viewBox="0 0 130 140" style={{ position:"absolute", left:anchor.left, top:anchor.top, zIndex:1 }}>
      <path d="M 118 128 C 95 90, 55 45, 22 18" stroke={_ARW} strokeWidth="2" fill="none" strokeLinecap="round" />
      <path d="M 45 10 L 22 18 L 35 45" stroke={_ARW} strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function GuideStep1({ onSkip, isGuest, onUpgrade, visible }) {
  if (!visible) return null;
  // marginTop clears the arrow's actual footprint (its box is 140px tall,
  // anchored just above the button) — the arrow's tail dips down further
  // than the old, much smaller arrow did, so this needed to grow to match
  // or the tail would cut through this text.
  return (
    <div style={{ marginTop:134, marginBottom:20, userSelect:"none" }}>
      <div style={{ ..._M, fontSize:13, color:_ARW, fontWeight:700 }}>start here</div>
      <div style={{ ..._M, fontSize:9, color:"#666", marginTop:4 }}>tap + Add to track your first machine</div>
      <div style={{ ..._M, fontSize:9, color:"#555", marginTop:2 }}>name &amp; type is all you need to begin</div>
      {isGuest && (
        <div style={{ ..._M, fontSize:10, color:"#444", marginTop:8 }}>
          guest: 3-machine limit ·{" "}
          <span onClick={onUpgrade} style={{ color:_ARW, cursor:"pointer" }}>create a free account →</span>
        </div>
      )}
      <button onClick={onSkip} style={{ ..._M, marginTop:12, background:"none", border:"none", color:"#333", fontSize:10, cursor:"pointer", padding:0, letterSpacing:"0.05em" }}>skip guide</button>
    </div>
  );
}

function GuideStep2({ onSkip }) {
  return (
    <div style={{ display:"flex", flexDirection:"column", alignItems:"center", marginBottom:14, userSelect:"none" }}>
      <span style={{ ..._M, fontSize:11, color:_ARW, fontWeight:700 }}>tap the card to explore</span>
      <span style={{ ..._M, fontSize:9, color:"#555", marginTop:4, textAlign:"center" }}>service history · timers · photos · invoices</span>
      <svg className="arrow-guide" width="30" height="42" viewBox="0 0 30 42" style={{ marginTop:8 }}>
        <path d="M 15 4 C 20 16, 11 25, 15 36" stroke={_ARW} strokeWidth="1.7" fill="none" strokeLinecap="round" />
        <path d="M 11 32 L 15 38 L 19 32" stroke={_ARW} strokeWidth="1.7" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      <button onClick={onSkip} style={{ ..._M, marginTop:10, background:"none", border:"none", color:"#333", fontSize:10, cursor:"pointer", padding:0, letterSpacing:"0.05em" }}>got it</button>
    </div>
  );
}

// Cross-tab destinations the Garage search bar's scope dropdown can reach
// into, beyond Garage's own machines (always searched, not a checkbox).
// Clients/Vehicles/Equipment/Tools are already loaded app-wide, so they
// match instantly; Parts and Wiki aren't, so those go through a debounced
// live query instead (see the effect below) — only fired when actually
// selected, not on every keystroke regardless of scope.
const CROSS_TAB_CATEGORIES=[
  {key:'clients',label:'Clients'},
  {key:'vehicles',label:'Vehicles'},
  {key:'equipment',label:'Equipment'},
  {key:'tools',label:'Tools'},
  {key:'parts',label:'Parts'},
  {key:'wiki',label:'Wiki'},
];

function Tracker({machines:allMachines,setMachines,company,profile,setProfile,clients,vehicles,equipment,tools,onJumpTo,isGuest,onGoToBilling,templateMachineId,onTemplateClear,active,initialSearch,onInitialSearchConsumed}){
  // Bulk-fetched (not per-machine) — a machine currently on the Bench,
  // booked into Storage, or Collected by a customer lives in that tab
  // instead, the same way a sold machine lives in Sold Items. Refetches on
  // every activation rather than just once on mount: like every other main
  // tab this stays mounted (display:none) for the whole session, so a
  // mount-only fetch would go stale the moment a machine moves out of
  // Garage from one of those other tabs instead of from here.
  const [awayIds,setAwayIds]=useState(()=>new Set());
  useEffect(()=>{
    if(!active) return;
    let alive=true;
    Promise.all([getAllActiveBookings(),getAllActiveCollections()]).then(([bookings,collections])=>{
      if(alive)setAwayIds(new Set([...bookings.map(b=>b.machine_id),...collections.map(c=>c.machine_id)]));
    });
    return ()=>{alive=false;};
  },[active]);
  // Sold machines stay in the shared machines[] state (so relisting from the
  // Sold Items tab can instantly clear their soldAt flag) but drop out of the
  // active Garage view here — same for a machine currently on the Bench, in
  // Storage, or Collected, which live in their own tabs instead.
  const machines=useMemo(()=>allMachines.filter(m=>!m.soldAt&&!m.onBench&&!awayIds.has(m.id)),[allMachines,awayIds]);
  const [showAdd,setShowAdd]=useState(false);
  const [prefill,setPrefill]=useState(null);
  const [showUpgrade,setShowUpgrade]=useState(false);
  const [saving,setSaving]=useState(false);
  const [dragIdx,setDragIdx]=useState(null);
  const [dragOver,setDragOver]=useState(null);
  const [showSort,setShowSort]=useState(false);
  const [sortBy,setSortBy]=useState(()=>getPref(profile,"trackerSort",null));
  // "list" (poster-card) view was removed — it duplicated the full MachineCard
  // overlay every tile already opens into. Migrate any saved pref to photo.
  const [view,setView]=useState(()=>{
    const v=getPref(profile,"trackerView","photo");
    return v==="list"?"photo":v;
  });
  const [tileOpen,setTileOpen]=useState(null);
  // Phone back button closes the full-screen tile overlay in one press instead
  // of just collapsing the card inside it (mirrors MachineCard's own cardOpen
  // history trick, one level up).
  useEffect(()=>{
    if(!tileOpen) return;
    history.pushState({trackerTileOpen:tileOpen},'');
    const onPop=e=>{
      if(e.state?.trackerTileOpen===tileOpen) return;
      setTileOpen(null);
    };
    window.addEventListener('popstate',onPop);
    return ()=>window.removeEventListener('popstate',onPop);
  },[tileOpen]);
  const closeTile=()=>{
    if(history.state?.trackerTileOpen===tileOpen) history.back();
    else setTileOpen(null);
  };
  const [search,setSearch]=useState("");
  // Consume-once, same idiom as templateMachineId/onTemplateClear above —
  // a global-search result jumping in here pre-fills this tab's own filter.
  useEffect(()=>{
    if(!initialSearch)return;
    setSearch(initialSearch);
    onInitialSearchConsumed?.();
  },[initialSearch]);

  // The search bar's scope selector — "Garage" (machines, always on) plus
  // whichever of Clients/Vehicles/Equipment/Tools/Parts/Wiki the user has
  // checked. Replaces the old standalone 🔍 header button/modal: same reach,
  // but integrated into the filter you're already typing into instead of a
  // separate overlay, and the header no longer has to make room for it.
  const [searchScope,setSearchScope]=useState(()=>new Set());
  const [scopeOpen,setScopeOpen]=useState(false);
  const scopeRef=useRef(null);
  useEffect(()=>{
    if(!scopeOpen)return;
    const onDocClick=e=>{ if(scopeRef.current&&!scopeRef.current.contains(e.target))setScopeOpen(false); };
    document.addEventListener('mousedown',onDocClick);
    return ()=>document.removeEventListener('mousedown',onDocClick);
  },[scopeOpen]);
  const toggleScope=key=>setSearchScope(prev=>{
    const next=new Set(prev);
    if(next.has(key))next.delete(key);else next.add(key);
    return next;
  });
  const allScoped=searchScope.size===CROSS_TAB_CATEGORIES.length;
  const toggleEverywhere=()=>setSearchScope(allScoped?new Set():new Set(CROSS_TAB_CATEGORIES.map(c=>c.key)));

  // Parts/Wiki aren't preloaded app-wide, so they're debounced live queries —
  // gated on actually being selected, not fired just because a scope of any
  // kind is active.
  const [liveResults,setLiveResults]=useState([]);
  useEffect(()=>{
    const query=search.trim();
    const wantParts=searchScope.has('parts');
    const wantWiki=searchScope.has('wiki');
    if(query.length<2||(!wantParts&&!wantWiki)){setLiveResults([]);return;}
    let cancelled=false;
    const timer=setTimeout(async()=>{
      const lower=query.toLowerCase();
      const [items,wikiRows]=await Promise.all([
        wantParts?getInventoryItems().catch(()=>[]):Promise.resolve([]),
        wantWiki?searchWiki(query).catch(()=>[]):Promise.resolve([]),
      ]);
      if(cancelled)return;
      const out=[];
      if(wantParts)(items||[]).forEach(i=>{
        const hay=[i.name,i.brand,i.partNumber,i.supplier].filter(Boolean).join(' ').toLowerCase();
        if(hay.includes(lower))out.push({key:'p:'+i.id,category:'Part',label:i.name||i.partNumber||'Part',sub:[i.brand,i.partNumber].filter(Boolean).join(' · '),jump:{tab:'workshop',subTab:'parts',query:i.name||query}});
      });
      if(wantWiki)(wikiRows||[]).slice(0,12).forEach(e=>{
        out.push({key:'w:'+e.id,category:'Wiki',label:[e.make,e.model].filter(Boolean).join(' ')||e.slug,sub:e.type||'',jump:{tab:'community',subTab:'wiki',query:e.slug}});
      });
      setLiveResults(out);
    },300);
    return ()=>{cancelled=true;clearTimeout(timer);};
  },[search,searchScope]);

  const crossTabResults=useMemo(()=>{
    const query=search.trim();
    if(query.length<2||searchScope.size===0)return [];
    const lower=query.toLowerCase();
    const out=[];
    if(searchScope.has('clients'))(clients||[]).forEach(c=>{
      if((c.name||'').toLowerCase().includes(lower)||(c.phone||'').includes(query)||(c.email||'').toLowerCase().includes(lower))
        out.push({key:'c:'+c.id,category:'Client',label:c.name,sub:c.phone||c.email||'',jump:{tab:'office',subTab:'clients',query:c.name}});
    });
    if(searchScope.has('vehicles'))(vehicles||[]).forEach(v=>{
      const hay=[v.name,v.make,v.model].filter(Boolean).join(' ').toLowerCase();
      if(hay.includes(lower))out.push({key:'v:'+v.id,category:'Vehicle',label:v.name||[v.make,v.model].filter(Boolean).join(' '),sub:[v.make,v.model].filter(Boolean).join(' '),jump:{tab:'workshop',subTab:'vehicles',query:v.name||query}});
    });
    if(searchScope.has('equipment'))(equipment||[]).forEach(e=>{
      const hay=[e.name,e.make,e.model,e.type].filter(Boolean).join(' ').toLowerCase();
      if(hay.includes(lower))out.push({key:'e:'+e.id,category:'Equipment',label:e.name||[e.make,e.model].filter(Boolean).join(' '),sub:e.type||'',jump:{tab:'workshop',subTab:'equipment',query:e.name||query}});
    });
    if(searchScope.has('tools'))(tools||[]).forEach(t=>{
      const hay=[t.name,t.brand,t.model,t.category].filter(Boolean).join(' ').toLowerCase();
      if(hay.includes(lower))out.push({key:'t:'+t.id,category:'Tool',label:t.name||[t.brand,t.model].filter(Boolean).join(' '),sub:t.category||'',jump:{tab:'workshop',subTab:'tools',query:t.name||query}});
    });
    return [...out,...liveResults].slice(0,40);
  },[search,searchScope,clients,vehicles,equipment,tools,liveResults]);

  const [tutDone,setTutDone]=useState(()=>getPref(profile,'rat_tut',false));
  const [tutCardOpened,setTutCardOpened]=useState(false);
  const skipTut=()=>{setTutDone(true);savePref(profile?.id,'rat_tut',true);};
  const tutStep=!tutDone?(machines.length===0?1:machines.length===1?2:0):0;

  // Measures the real +Add button position so GuideStep1's arrow can anchor
  // to it precisely — flexbox right-alignment isn't reliable here since the
  // guide's own text (e.g. "guest: 3-machine limit…") can be wider than the
  // button row it's meant to point at.
  const contentRef=useRef(null);
  const addBtnRef=useRef(null);
  const [addBtnAnchor,setAddBtnAnchor]=useState(null);
  useLayoutEffect(()=>{
    if(tutStep!==1){setAddBtnAnchor(null);return;}
    const measure=()=>{
      if(!addBtnRef.current||!contentRef.current)return;
      const btn=addBtnRef.current.getBoundingClientRect();
      const container=contentRef.current.getBoundingClientRect();
      // The arrow's tip sits at local (22,18) in its own 130x140 box (see
      // GuideArrow) — anchored off the button's RIGHT edge (not left) so the
      // tail sweeps down further to the right, clear of the guide text below
      // rather than crossing through it.
      const btnRightX=btn.right-container.left;
      setAddBtnAnchor({left:btnRightX-8,top:btn.bottom-container.top-4});
    };
    measure();
    window.addEventListener('resize',measure);
    return ()=>window.removeEventListener('resize',measure);
  },[tutStep,isGuest]);

  const clientMap = useMemo(() => Object.fromEntries((clients||[]).map(c => [c.id, c.name])), [clients]);
  const setViewP=v=>{setView(v);savePref(profile?.id,"trackerView",v);};
  const setSortByP=v=>{setSortBy(v);savePref(profile?.id,"trackerSort",v??null);};

  const SORT_OPTS=[
    {k:"name_az",l:"Name A → Z"},
    {k:"name_za",l:"Name Z → A"},
    {k:"type",l:"Machine Type"},
    {k:"due",l:"Due Date (Soonest)"},
    {k:"newest",l:"Date Added (Newest)"},
    {k:"oldest",l:"Date Added (Oldest)"},
    {k:"rage_hi",l:"Rage ☠️ (Highest)"},
    {k:"rage_lo",l:"Rage ☠️ (Lowest)"},
  ];

  const searchQuery=search.trim();
  const searched=searchQuery
    ?machines.filter(m=>machineMatchesQuery(m,searchQuery))
    :machines;
  // Same tokenizer the wiki search uses (a single plain substring), and the
  // exact same hl() highlight component, so matches render identically.
  const searchTokens=tokenizeSearch(search);
  const sorted=sortBy?[...searched].sort((a,b)=>{
    if(sortBy==="name_az") return (a.name||"").localeCompare(b.name||"");
    if(sortBy==="name_za") return (b.name||"").localeCompare(a.name||"");
    if(sortBy==="type") return (a.type||"").localeCompare(b.type||"");
    if(sortBy==="due"){
      const ad=a.dueDate?new Date(a.dueDate).getTime():Infinity;
      const bd=b.dueDate?new Date(b.dueDate).getTime():Infinity;
      return ad-bd;
    }
    if(sortBy==="newest") return new Date(b.createdAt||0)-new Date(a.createdAt||0);
    if(sortBy==="oldest") return new Date(a.createdAt||0)-new Date(b.createdAt||0);
    if(sortBy==="rage_hi") return (b.rage||0)-(a.rage||0);
    if(sortBy==="rage_lo") return (a.rage||0)-(b.rage||0);
    return 0;
  }):searched;


  const addM=async m=>{
    setSaving(true);
    try{
      await upsertMachine(m);
      setMachines(prev=>[m,...prev]);
      setShowAdd(false);
    }catch(e){alert("Save failed: "+e.message);}
    setSaving(false);
  };
  const updateM=async m=>{
    try{
      await upsertMachine(m);
      setMachines(prev=>prev.map(x=>x.id===m.id?m:x));
    }catch(e){alert("Save failed: "+e.message);}
  };
  const moveToBench=async m=>{
    const u={...m,onBench:true};
    try{
      await upsertMachine(u);
      setMachines(prev=>prev.map(x=>x.id===m.id?u:x));
      setTileOpen(null);
    }catch(e){alert("Save failed: "+e.message);}
  };
  const deleteM=async m=>{
    try{
      await deleteMachineApi(m.id);
      setMachines(prev=>prev.filter(x=>x.id!==m.id));
    }catch(e){toastError("Delete failed: "+e.message);return;}

    // Goes to Recently Deleted for 30 days (supabase/recently_deleted.sql) rather
    // than a scary "are you sure?" wall — this toast's Undo is the fast path,
    // Settings → Recently Deleted is the slow path if it's missed.
    const logId=await findMyRecentlyDeletedLogId('machines',m.id);
    if(!logId)return; // snapshot lookup failed — delete already succeeded either way
    toastUndo(`${m.name||'Machine'} deleted`, async()=>{
      try{
        const {record}=await restoreDeletedRecord(logId);
        setMachines(prev=>[record||m,...prev]);
      }catch(e){toastError("Couldn't undo — "+e.message);}
    });
  };

  const onDragStart=(e,idx)=>{setDragIdx(idx);e.dataTransfer.effectAllowed="move";};
  const onDragOver=(e,idx)=>{e.preventDefault();setDragOver(idx);};
  const onDrop=(e,idx)=>{
    e.preventDefault();
    if(dragIdx===null||dragIdx===idx)return;
    const reordered=[...machines];
    const [moved]=reordered.splice(dragIdx,1);
    reordered.splice(idx,0,moved);
    // machines here is the active-only view — merge back any sold machines
    // (kept out of this list) so reordering the Garage doesn't drop them
    // from shared state.
    setMachines(prev=>[...reordered,...prev.filter(m=>m.soldAt)]);
    setDragIdx(null);setDragOver(null);
  };
  const onDragEnd=()=>{setDragIdx(null);setDragOver(null);};

  useEffect(()=>{
    if(!templateMachineId) return;
    supabase.rpc('get_public_machine',{p_id:templateMachineId}).then(({data})=>{
      if(data){
        setPrefill({name:data.name,type:data.type||"",make:data.make||"",model:data.model||"",year:data.year||"",desc:data.notes||""});
        setShowAdd(true);
      }
      onTemplateClear?.();
    }).catch(e=>{
      console.error("template fetch:",e);
      onTemplateClear?.();
    });
  },[templateMachineId]);

  return (
    <div ref={contentRef} style={{padding:16,flex:1,position:"relative"}}>
      {showAdd&&<ErrorBoundary><MachineForm existing={prefill||undefined} onSave={m=>{addM(m);setPrefill(null);}} onClose={()=>{setShowAdd(false);setPrefill(null);}} company={company} units={profile?.units||"metric"} profile={profile} isGuest={isGuest}/></ErrorBoundary>}
      {showSort&&(
        <div style={ovly} onClick={()=>setShowSort(false)}>
          <div style={{...mdl,maxHeight:"70vh"}} onClick={ev=>ev.stopPropagation()}>
            <div style={mdlH}>
              <b style={{fontSize:13,textTransform:"uppercase",letterSpacing:"0.1em"}}>Sort Machines</b>
              <button style={{...btnG,...sm}} onClick={()=>setShowSort(false)}>✕</button>
            </div>
            <div style={{...mdlB,paddingTop:8}}>
              <label style={{display:"flex",alignItems:"center",gap:10,padding:"9px 0",borderBottom:"1px solid #1a1a1a",cursor:"pointer"}} onClick={()=>setSortByP(null)}>
                <input type="radio" readOnly checked={sortBy===null} style={{accentColor:ACC,width:15,height:15}} />
                <span style={{fontSize:11,color:sortBy===null?TXT:MUT,fontFamily:"'IBM Plex Mono',monospace"}}>Manual order (drag)</span>
              </label>
              {SORT_OPTS.map(o=>(
                <label key={o.k} style={{display:"flex",alignItems:"center",gap:10,padding:"9px 0",borderBottom:"1px solid #1a1a1a",cursor:"pointer"}} onClick={()=>setSortByP(o.k)}>
                  <input type="radio" readOnly checked={sortBy===o.k} style={{accentColor:ACC,width:15,height:15}} />
                  <span style={{fontSize:11,color:sortBy===o.k?TXT:MUT,fontFamily:"'IBM Plex Mono',monospace"}}>{o.l}</span>
                </label>
              ))}
            </div>
            <div style={mdlF}>
              <button style={btnG} onClick={()=>{setSortByP(null);setShowSort(false);}}>Reset</button>
              <button style={btnA} onClick={()=>setShowSort(false)}>Done</button>
            </div>
          </div>
        </div>
      )}
      <div style={{position:"relative",marginBottom:8}}>
        <input
          style={{...inp,fontSize:11,paddingRight:searchScope.size>0?96:76}}
          placeholder="Search name, make, model, or any spec (e.g. plug gap, tyre size)…"
          value={search} onChange={e=>setSearch(e.target.value)}
        />
        <button
          onClick={()=>setScopeOpen(o=>!o)}
          title="Choose where else to search"
          style={{
            position:"absolute",right:6,top:"50%",transform:"translateY(-50%)",
            background:searchScope.size>0?ACC+"22":"#1a1a1a",
            border:"1px solid "+(searchScope.size>0?ACC+"88":BRD),
            borderRadius:2,color:searchScope.size>0?ACC:MUT,fontSize:9,
            padding:"4px 8px",cursor:"pointer",letterSpacing:"0.03em",
            fontFamily:"'IBM Plex Mono',monospace",whiteSpace:"nowrap",
          }}
        >
          {searchScope.size===0?"Garage":allScoped?"Everywhere":`Garage +${searchScope.size}`} ▾
        </button>
        {scopeOpen&&(
          <div ref={scopeRef} style={{position:"absolute",top:"100%",right:0,marginTop:4,background:SURF,border:"1px solid "+BRD,borderRadius:3,zIndex:20,width:200,boxShadow:"0 4px 16px rgba(0,0,0,0.5)"}}>
            <div style={{padding:"8px 10px",fontSize:8,color:MUT,letterSpacing:"0.1em",textTransform:"uppercase",borderBottom:"1px solid "+BRD}}>Also search in</div>
            <label style={{display:"flex",alignItems:"center",gap:8,padding:"8px 10px",cursor:"pointer",borderBottom:"1px solid #1a1a1a"}}>
              <input type="checkbox" checked={allScoped} onChange={toggleEverywhere} style={{accentColor:ACC,width:14,height:14}} />
              <span style={{fontSize:11,color:TXT,fontWeight:700}}>🌐 Everywhere</span>
            </label>
            {CROSS_TAB_CATEGORIES.map(c=>(
              <label key={c.key} style={{display:"flex",alignItems:"center",gap:8,padding:"7px 10px",cursor:"pointer"}}>
                <input type="checkbox" checked={searchScope.has(c.key)} onChange={()=>toggleScope(c.key)} style={{accentColor:ACC,width:14,height:14}} />
                <span style={{fontSize:11,color:TXT}}>{c.label}</span>
              </label>
            ))}
            <div style={{padding:"6px 10px",fontSize:8,color:MUT,borderTop:"1px solid "+BRD}}>Garage is always searched</div>
          </div>
        )}
      </div>
      {crossTabResults.length>0&&(
        <div style={{border:"1px solid "+BRD,borderRadius:2,marginBottom:10,maxHeight:240,overflowY:"auto"}}>
          {crossTabResults.map(r=>(
            <div key={r.key} onClick={()=>onJumpTo?.(r.jump)}
              style={{display:"flex",alignItems:"center",gap:10,padding:"8px 10px",borderBottom:"1px solid #1a1a1a",cursor:"pointer"}}
              onMouseEnter={e=>e.currentTarget.style.background="#161616"} onMouseLeave={e=>e.currentTarget.style.background="transparent"}
            >
              <span style={{fontSize:7,color:ACC,letterSpacing:"0.08em",textTransform:"uppercase",fontWeight:700,border:"1px solid "+ACC+"55",borderRadius:2,padding:"2px 5px",flexShrink:0}}>{r.category}</span>
              <div style={{minWidth:0,flex:1}}>
                <div style={{fontSize:11,color:TXT,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{r.label}</div>
                {r.sub&&<div style={{fontSize:9,color:MUT,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{r.sub}</div>}
              </div>
            </div>
          ))}
        </div>
      )}
      <div style={{display:"flex",flexDirection:"column",alignItems:"flex-start",gap:8,marginBottom:12}}>
        <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap",rowGap:6}}>
          <span style={{fontSize:9,letterSpacing:"0.18em",textTransform:"uppercase",color:ACC,whiteSpace:"nowrap"}}>Machines</span>
          {sortBy&&<span style={{fontSize:10,color:ACC,letterSpacing:"0.1em",textTransform:"uppercase",border:"1px solid "+ACC+"44",borderRadius:2,padding:"1px 5px",whiteSpace:"nowrap"}}>{SORT_OPTS.find(o=>o.k===sortBy)?.l}</span>}
        </div>
        <div style={{display:"flex",gap:6,alignItems:"center",flexWrap:"wrap"}}>
          <button style={{...btnG,color:sortBy?ACC:MUT,alignSelf:"stretch"}} onClick={()=>setShowSort(true)} title="Sort machines">⚙️</button>
          <button onClick={()=>setViewP(view==="grid"?"compact":view==="compact"?"photo":"grid")} style={{...btnG,minWidth:36,alignSelf:"stretch",color:ACC}} title={view==="grid"?"Compact list":view==="compact"?"Photo list":"Grid view"}>{view==="grid"?"≡":view==="compact"?"▣":"☰"}</button>
          {isGuest&&machines.length>=3
            ? <div style={{display:"flex",alignItems:"center",gap:8}}>
                <span style={{fontSize:9,color:MUT,letterSpacing:"0.06em"}}>3 machine guest limit</span>
                <button style={{...btnA,...sm,background:"#1a7a3a",borderColor:"#1a7a3a"}} onClick={()=>setShowUpgrade(true)}>Create a free account →</button>
              </div>
            : <button ref={addBtnRef} style={{...btnA, minHeight:44, display:"flex", alignItems:"center"}} onClick={()=>setShowAdd(true)}>+ Add</button>}
        </div>
      </div>
      <GuideArrow anchor={tutStep===1?addBtnAnchor:null}/>
      <GuideStep1 onSkip={skipTut} isGuest={isGuest} onUpgrade={()=>setShowUpgrade(true)} visible={tutStep===1}/>
      {saving&&<div style={{fontSize:10,color:MUT,marginBottom:10}}>Saving...</div>}
      {tutStep===0&&machines.length===0&&<Empty icon="🔧" t="No machines yet" sub="Tap + Add above to add your first machine — mowers, bikes, generators, anything you work on." />}
      {machines.length>0&&sorted.length===0&&<div style={{fontSize:10,color:MUT,textAlign:"center",padding:"24px 0"}}>No machines match your filter.</div>}
      {tutStep===2&&!tutCardOpened&&<GuideStep2 onSkip={skipTut}/>}
      {/* Shared tile overlay — used by both grid and compact views */}
      {tileOpen&&(()=>{const m=sorted.find(x=>x.id===tileOpen);return m?(
        <div style={{position:"fixed",inset:0,background:"#000",zIndex:200,overflowY:"auto",overscrollBehavior:"contain"}}>
          <div style={{maxWidth:640,margin:"0 auto",padding:"8px 8px 0"}}>
            <MachineCard machine={m} onUpdate={u=>{updateM(u);}} onDelete={d=>{deleteM(d);setTileOpen(null);}} company={company} profile={profile} clients={clients} isGuest={isGuest} showGuide={tutStep===2} onTutDismiss={skipTut} onCardOpened={()=>setTutCardOpened(true)} initialOpen hideCollapse onClose={closeTile} searchQuery={searchQuery} searchTokens={searchTokens} onMoveToBench={moveToBench} onBookIn={id=>setAwayIds(prev=>new Set(prev).add(id))}/>
          </div>
        </div>
      ):null;})()}
      {(()=>{
        const Comp = view==="grid"?MachineTile:view==="compact"?MachineRow:MachinePhotoRow;
        const wrapStyle = view==="grid"
          // alignItems:"start" stops a tile with a long wrapped name from
          // stretching its whole grid row — without it every tile in that
          // row grows to match the tallest one, leaving dead space under
          // shorter tiles instead of each one just hugging its own content.
          ?{display:"grid",gridTemplateColumns:"repeat(2,1fr)",gap:8,alignItems:"start"}
          :{borderTop:"1px solid "+BRD,borderRadius:3,overflow:"hidden"};
        return (
          <div style={wrapStyle}>
            {sorted.map((m,idx)=>(
              <div key={m.id} draggable={!sortBy}
                onDragStart={sortBy?undefined:e=>onDragStart(e,idx)}
                onDragOver={sortBy?undefined:e=>onDragOver(e,idx)}
                onDrop={sortBy?undefined:e=>onDrop(e,idx)}
                onDragEnd={sortBy?undefined:onDragEnd}
                style={sortBy?undefined:{opacity:dragIdx===idx?0.4:1,outline:dragOver===idx&&dragIdx!==idx?"2px solid "+ACC:"2px solid transparent",outlineOffset:-2,transition:"opacity 0.15s,outline-color 0.1s"}}
              >
                <Comp machine={m} onClick={()=>setTileOpen(m.id)} clientName={m.clientId?clientMap[m.clientId]:null} searchQuery={searchQuery} searchTokens={searchTokens}/>
              </div>
            ))}
          </div>
        );
      })()}
      {showUpgrade&&<GuestUpgradeModal profile={profile} setProfile={setProfile} onClose={()=>setShowUpgrade(false)}/>}
    </div>
  );
}
export default Tracker;